import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AnalyticsEntry, AnalyticsInsight, Platform } from '../types/analytics';

interface AnalyticsStore {
  entries: AnalyticsEntry[];
  addEntry: (entry: Omit<AnalyticsEntry, 'id' | 'date'>) => void;
  updateEntry: (id: string, entry: Partial<AnalyticsEntry>) => void;
  deleteEntry: (id: string) => void;
  getEntriesByPlatform: (platform: Platform) => AnalyticsEntry[];
  getRecentEntries: (count?: number) => AnalyticsEntry[];
  generateInsights: () => AnalyticsInsight[];
}

export const useAnalyticsStore = create<AnalyticsStore>()(
  persist(
    (set, get) => ({
      entries: [],
      
      addEntry: (entry) => set((state) => ({
        entries: [
          {
            ...entry,
            id: crypto.randomUUID(),
            date: new Date().toISOString()
          },
          ...state.entries
        ]
      })),
      
      updateEntry: (id, updatedEntry) => set((state) => ({
        entries: state.entries.map((entry) =>
          entry.id === id ? { ...entry, ...updatedEntry } : entry
        )
      })),
      
      deleteEntry: (id) => set((state) => ({
        entries: state.entries.filter((entry) => entry.id !== id)
      })),
      
      getEntriesByPlatform: (platform) => {
        return get().entries.filter(entry => entry.platform === platform);
      },
      
      getRecentEntries: (count = 10) => {
        return get().entries
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, count);
      },
      
      generateInsights: () => {
        const entries = get().entries;
        if (entries.length < 2) return [];
        
        const insights: AnalyticsInsight[] = [];
        const latestEntry = entries[0];
        const previousEntry = entries[1];
        
        // Engagement rate comparison
        const engagementImprovement = (
          (latestEntry.engagementRate - previousEntry.engagementRate) / 
          previousEntry.engagementRate * 100
        );
        
        if (Math.abs(engagementImprovement) > 10) {
          insights.push({
            type: engagementImprovement > 0 ? 'success' : 'warning',
            message: `Seu último post teve ${Math.abs(engagementImprovement).toFixed(1)}% ${
              engagementImprovement > 0 ? 'mais' : 'menos'
            } engajamento que o anterior.`,
            metric: 'engagementRate',
            improvement: engagementImprovement
          });
        }
        
        // Views trend
        const viewsImprovement = (
          (latestEntry.views - previousEntry.views) / 
          previousEntry.views * 100
        );
        
        if (Math.abs(viewsImprovement) > 20) {
          insights.push({
            type: viewsImprovement > 0 ? 'success' : 'warning',
            message: `Suas visualizações ${
              viewsImprovement > 0 ? 'aumentaram' : 'diminuíram'
            } em ${Math.abs(viewsImprovement).toFixed(1)}%.`,
            metric: 'views',
            improvement: viewsImprovement
          });
        }
        
        return insights;
      }
    }),
    {
      name: 'analytics-storage',
      version: 1,
      migrate: (persistedState: any, version: number) => persistedState
    }
  )
);